import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type ApiKeyContract,
  type CreatedApiKeyContract,
  PUBLIC_API_SCOPES,
  type PublicApiScope,
} from '@nest-react/domain';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useApiRequest } from '~/app/providers';
import { Icon } from '~/components/ui/Icon';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { createDeveloperApiRepository } from '~/repositories/developerApiRepository';
import { useBillingUpgrade } from '~/features/billing/BillingUpgradeProvider';
import {
  useActiveWorkspace,
  useWorkspaceBillingSummary,
  useWorkspaceLocale,
} from '~/stores/workspace';
import { hasDeveloperApiAccess } from './developerApiAccess';

const scopeLabel = (scope: PublicApiScope): string => scope.replace(':', ' · ');

export const DeveloperApiPage = (): React.ReactElement => {
  const { t } = useTranslation('developerApi');
  const request = useApiRequest();
  const activeWorkspace = useActiveWorkspace();
  const locale = useWorkspaceLocale();
  const { openUpgrade } = useBillingUpgrade();
  const repository = useMemo(
    () => createDeveloperApiRepository(request),
    [request]
  );
  const workspaceId = activeWorkspace?.id;
  const {
    summary: billing,
    loading: billingLoading,
    error: billingError,
  } = useWorkspaceBillingSummary(workspaceId);
  const [keys, setKeys] = useState<ApiKeyContract[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [minExpiry] = useState(() =>
    new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)
  );
  const [scopes, setScopes] = useState<PublicApiScope[]>([
    ...PUBLIC_API_SCOPES,
  ]);
  const [created, setCreated] = useState<CreatedApiKeyContract | null>(null);
  const [copied, setCopied] = useState(false);
  const [quickStartKind, setQuickStartKind] = useState<'image' | 'video'>(
    'image'
  );
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale),
    [locale]
  );
  const loading = keysLoading || billingLoading;

  useEffect(() => {
    if (!workspaceId) return undefined;
    let current = true;
    const timer = window.setTimeout(() => {
      setKeysLoading(true);
      setError('');
      void repository
        .list(workspaceId)
        .then(nextKeys => {
          if (current) setKeys(nextKeys);
        })
        .catch(reason => {
          if (current)
            setError(reason instanceof Error ? reason.message : t('loadError'));
        })
        .finally(() => {
          if (current) setKeysLoading(false);
        });
    }, 0);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [repository, t, workspaceId]);

  useEffect(() => {
    if (created) copyButtonRef.current?.focus();
  }, [created]);

  if (!activeWorkspace) {
    return <p className="text-sm text-muted">{t('chooseWorkspace')}</p>;
  }

  const hasApiAccess = billing
    ? hasDeveloperApiAccess(billing.subscription)
    : false;

  if (loading) {
    return (
      <p
        aria-live="polite"
        className="mx-auto w-full max-w-5xl rounded-2xl border border-border bg-elevated p-6 text-sm text-muted"
      >
        {t('loading')}
      </p>
    );
  }

  if (!billing) {
    return (
      <p
        className="mx-auto w-full max-w-5xl rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger"
        role="alert"
      >
        {billingError || error || t('loadError')}
      </p>
    );
  }

  if (!hasApiAccess) {
    return (
      <div className="mx-auto grid w-full max-w-5xl gap-8">
        <header className="grid gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            {t('eyebrow')}
          </p>
          <h1 className="text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {t('title')}
          </h1>
          <p className="max-w-2xl text-sm leading-6 text-muted">
            {t('description', { workspace: activeWorkspace.name })}
          </p>
        </header>
        <section className="relative overflow-hidden rounded-2xl border border-primary/25 bg-elevated p-6 shadow-sm sm:p-8">
          <div
            aria-hidden="true"
            className="absolute -right-12 -top-16 size-48 rounded-full bg-primary/10 blur-3xl"
          />
          <div className="relative max-w-2xl">
            <span className="inline-flex rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {t('proBenefit')}
            </span>
            <h2 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
              {t('lockedTitle')}
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted">
              {t('lockedDescription')}
            </p>
            <button
              className="mt-6 inline-flex min-h-11 touch-manipulation items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-[filter] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none"
              onClick={() => openUpgrade()}
              type="button"
            >
              {t('upgradeToPro')}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const toggleScope = (scope: PublicApiScope): void => {
    setScopes(current =>
      current.includes(scope)
        ? current.filter(item => item !== scope)
        : [...current, scope]
    );
  };
  const createKey = async (): Promise<void> => {
    if (!name.trim() || scopes.length === 0 || creating) return;
    setCreating(true);
    setError('');
    try {
      const key = await repository.create(activeWorkspace.id, {
        name: name.trim(),
        scopes,
        ...(expiresAt
          ? { expiresAt: new Date(`${expiresAt}T23:59:59`).toISOString() }
          : {}),
      });
      setKeys(current => [key, ...current]);
      setCreated(key);
      setName('');
      setExpiresAt('');
      setCopied(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t('createError'));
    } finally {
      setCreating(false);
    }
  };
  const revoke = async (key: ApiKeyContract): Promise<void> => {
    if (
      key.revokedAt ||
      !window.confirm(t('revokeConfirm', { name: key.name }))
    )
      return;
    try {
      const updated = await repository.revoke(activeWorkspace.id, key.id);
      setKeys(current =>
        current.map(item => (item.id === updated.id ? updated : item))
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t('revokeError'));
    }
  };

  return (
    <div className="mx-auto grid w-full max-w-5xl gap-8">
      <header className="grid gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          {t('eyebrow')}
        </p>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              {t('title')}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-muted">
              {t('description', { workspace: activeWorkspace.name })}
            </p>
          </div>
          <span className="rounded-full border border-border bg-elevated px-3 py-1.5 text-xs text-muted">
            {t('activeKeys', {
              count: keys.filter(
                key =>
                  !key.revokedAt &&
                  (!key.expiresAt || new Date(key.expiresAt) > new Date())
              ).length,
            })}
          </span>
        </div>
      </header>

      <section className="overflow-hidden rounded-2xl border border-border bg-elevated shadow-sm">
        <div className="grid gap-5 p-5 sm:grid-cols-[1fr_13rem_auto] sm:items-end sm:p-6">
          <label className="grid gap-2 text-sm font-medium text-foreground">
            {t('keyName')}
            <input
              autoComplete="off"
              className="h-11 rounded-xl border border-border bg-surface px-3 font-normal outline-none transition focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
              maxLength={80}
              name="api-key-name"
              onChange={event => setName(event.target.value)}
              placeholder={t('keyNamePlaceholder')}
              spellCheck={false}
              value={name}
            />
          </label>
          <label className="grid gap-2 text-sm font-medium text-foreground">
            {t('expiresAt')}
            <input
              autoComplete="off"
              className="h-11 rounded-xl border border-border bg-surface px-3 font-normal text-foreground outline-none transition focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
              min={minExpiry}
              name="api-key-expiry"
              onChange={event => setExpiresAt(event.target.value)}
              style={expiresAt ? undefined : { color: 'transparent' }}
              type="date"
              value={expiresAt}
            />
          </label>
          <button
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-semibold text-white transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!name.trim() || scopes.length === 0 || creating}
            onClick={() => void createKey()}
            type="button"
          >
            <Icon className="size-4" name="plus" />
            {creating ? t('creating') : t('createKey')}
          </button>
        </div>
        <fieldset className="flex flex-wrap gap-2 border-t border-border-subtle px-5 py-4 sm:px-6">
          <legend className="sr-only">{t('permissions')}</legend>
          {PUBLIC_API_SCOPES.map(scope => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-muted transition hover:border-primary/40 hover:text-foreground"
              key={scope}
            >
              <input
                checked={scopes.includes(scope)}
                className="accent-primary"
                name="api-key-scopes"
                onChange={() => toggleScope(scope)}
                type="checkbox"
                value={scope}
              />
              <span className="font-mono">{scopeLabel(scope)}</span>
            </label>
          ))}
        </fieldset>
      </section>

      {error && (
        <p
          aria-live="polite"
          className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger"
          role="alert"
        >
          {error}
        </p>
      )}

      <section aria-labelledby="api-keys-heading" className="grid gap-3">
        <div className="flex items-center justify-between">
          <h2
            className="text-lg font-semibold text-foreground"
            id="api-keys-heading"
          >
            {t('keysTitle')}
          </h2>
          <span className="text-xs text-subtle">{t('maxKeys')}</span>
        </div>
        {keys.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-elevated p-8 text-center">
            <p className="font-medium text-foreground">{t('emptyTitle')}</p>
            <p className="mt-1 text-sm text-muted">{t('emptyDescription')}</p>
          </div>
        ) : (
          <div className="grid gap-3">
            {keys.map(key => (
              <article
                className="grid gap-4 rounded-2xl border border-border bg-elevated p-5 sm:grid-cols-[1fr_auto] sm:items-center"
                key={key.id}
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="break-words font-semibold text-foreground">
                      {key.name}
                    </h3>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${key.revokedAt || (key.expiresAt && new Date(key.expiresAt) <= new Date()) ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'}`}
                    >
                      {key.revokedAt
                        ? t('revoked')
                        : key.expiresAt && new Date(key.expiresAt) <= new Date()
                          ? t('expired')
                          : t('active')}
                    </span>
                  </div>
                  <code className="mt-3 block overflow-x-auto rounded-lg bg-surface px-3 py-2 font-mono text-xs tracking-wide text-muted">
                    <span translate="no">
                      mylo_live_{key.prefix}_••••••••{key.lastFour}
                    </span>
                  </code>
                  <p className="mt-3 text-xs text-subtle">
                    {t('keyMeta', {
                      creator: key.createdBy.displayName,
                      date: dateFormatter.format(new Date(key.createdAt)),
                      lastUsed: key.lastUsedAt
                        ? dateFormatter.format(new Date(key.lastUsedAt))
                        : t('never'),
                    })}
                  </p>
                  <p className="mt-2 font-mono text-[11px] text-subtle">
                    {key.scopes.join(' · ')}
                  </p>
                </div>
                <button
                  className="rounded-lg border border-border px-3 py-2 text-sm text-muted transition hover:border-danger/40 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={Boolean(key.revokedAt)}
                  onClick={() => void revoke(key)}
                  type="button"
                >
                  {t('revoke')}
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-foreground">{t('quickStart')}</h2>
          <Link
            className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-border px-3 text-sm font-medium text-foreground transition hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            to={`/workspaces/developer-api/docs#${quickStartKind === 'image' ? 'images' : 'videos'}`}
          >
            <Icon className="size-4" name="document" />
            {t('viewApiDocs')}
            <span aria-hidden="true">→</span>
          </Link>
        </div>
        <p className="mt-1 text-sm text-muted">{t('quickStartDescription')}</p>
        <div
          aria-label={t('quickStart')}
          className="mt-4 flex gap-2"
          role="tablist"
        >
          {(['image', 'video'] as const).map(kind => (
            <button
              aria-controls={`quick-start-${kind}`}
              aria-selected={quickStartKind === kind}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${quickStartKind === kind ? 'bg-primary text-white' : 'border border-border text-muted hover:border-primary/40 hover:text-foreground'}`}
              key={kind}
              id={`quick-start-tab-${kind}`}
              onClick={() => setQuickStartKind(kind)}
              role="tab"
              type="button"
            >
              {kind === 'image' ? t('imageExample') : t('videoExample')}
            </button>
          ))}
        </div>
        <pre
          aria-labelledby={`quick-start-tab-${quickStartKind}`}
          className="mt-4 overflow-x-auto rounded-xl bg-foreground p-4 text-xs leading-6 text-surface"
          id={`quick-start-${quickStartKind}`}
          role="tabpanel"
        >
          <code translate="no">
            {quickStartKind === 'image'
              ? `curl -X POST https://<region-api-host>/v1/images/generations \\
  -H "Authorization: Bearer $MYLO_API_KEY" \\
  -H "Idempotency-Key: request-001" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"A cinematic product photograph","aspectRatio":"1:1"}'`
              : `curl -X POST https://<region-api-host>/v1/videos/generations \\
  -H "Authorization: Bearer $MYLO_API_KEY" \\
  -H "Idempotency-Key: video-001" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"A playful four-second short film","duration":4,"ratio":"16:9"}'`}
          </code>
        </pre>
      </section>

      <Dialog
        onOpenChange={open => {
          if (!open) setCreated(null);
        }}
        open={Boolean(created)}
      >
        {created && (
          <DialogContent
            className="rounded-2xl border-border bg-elevated shadow-2xl"
            onOpenAutoFocus={event => {
              event.preventDefault();
              copyButtonRef.current?.focus();
            }}
            showCloseButton={false}
          >
            <DialogHeader>
              <DialogTitle className="text-xl text-foreground">
                {t('createdTitle')}
              </DialogTitle>
              <DialogDescription className="text-sm leading-6 text-muted">
                {t('createdDescription')}
              </DialogDescription>
            </DialogHeader>
            <code
              className="block overflow-x-auto rounded-xl border border-primary/20 bg-primary/5 p-4 font-mono text-sm text-foreground"
              translate="no"
            >
              {created.secret}
            </code>
            <DialogFooter>
              <DialogClose asChild>
                <button
                  className="rounded-xl border border-border px-4 py-2 text-sm text-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  type="button"
                >
                  {t('done')}
                </button>
              </DialogClose>
              <button
                aria-live="polite"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(created.secret)
                    .then(() => setCopied(true));
                }}
                ref={copyButtonRef}
                type="button"
              >
                {copied ? t('copied') : t('copyKey')}
              </button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};
